import {
  type Dictionary,
  type DictionarySource,
  loadDictionary,
} from "@kensio/pinyinjs";

import { DictionaryCache } from "./dictionary-cache.js";
import {
  cachedDictionaries,
  siteSource,
  tierUrls,
} from "./dictionary-source.js";

/**
 * Getting the dictionary into the browser.
 *
 * The conversion runs on the visitor's machine, which is the reason this tool
 * can exist at all after the server it used to need went away. The cost of
 * that is a dictionary download, and everything here is about keeping it
 * honest: one tier by default, a bigger one only when somebody asks, each
 * fetched at most once in a page and at most once on a device.
 *
 * "At most once on a device" is what `DictionaryCache` adds. See that module
 * for why the HTTP cache, which already keeps these files for a year, is not
 * enough on its own. What the files are called and where they sit is
 * `dictionary-source.ts`.
 */

/** A tier this site serves. See `src/assets/dictionary-data.ts`. */
export type ServedTier = "standard" | "full";

/**
 * The tier a page loads on arrival.
 *
 * `standard` is 66,730 entries and about 400 KB compressed. It holds every
 * character together with the fifty thousand most common words, which is what
 * decides a pronunciation: 长 is `cháng` or `zhǎng` depending on the word it
 * sits in, and the words that settle that are the common ones.
 *
 * `full` is 462,000 entries and about 2.6 MB, and what the extra 400,000 buy
 * is mostly names: people, places, chemical compounds, book titles. Real, but
 * not worth 2.2 MB of somebody's mobile data before they have typed anything.
 */
export const defaultTier: ServedTier = "standard";

/**
 * Loads tiers, and remembers the ones it has loaded.
 *
 * The promise is cached rather than its result, so that two things asking
 * during the same load share one request. A failed load is dropped from the
 * cache so that asking again retries: a rejected promise kept forever would
 * turn one flaky request into a permanently broken tier.
 *
 * Two kinds of remembering, then. `has` is this page's, and is gone when the
 * page is; `isStored` is the device's, and is what a later visit can still ask.
 */
export class DictionaryLoader {
  readonly #baseUrl: string;
  readonly #cache: DictionaryCache;
  readonly #source: DictionarySource;
  readonly #loading = new Map<ServedTier, Promise<Dictionary>>();

  constructor(baseUrl: string) {
    this.#baseUrl = baseUrl;
    this.#cache = new DictionaryCache(cachedDictionaries(baseUrl));
    this.#source = siteSource(baseUrl, this.#cache);
  }

  /**
   * Fetch a tier, forgetting it again if the fetch fails.
   *
   * The forgetting is what makes {@link load} retryable: a rejected promise
   * kept in the cache forever would turn one flaky request into a permanently
   * broken tier.
   */
  async #fetch(tier: ServedTier): Promise<Dictionary> {
    try {
      return await loadDictionary(this.#source, tier);
    } catch (error) {
      this.#loading.delete(tier);
      throw error;
    }
  }

  /**
   * The source these tiers come from, for a caller wanting a file beside them.
   *
   * The script converter needs `script.map` as well as a dictionary, and it
   * wants it through the same store: it is published under the same versioned
   * path and is worth keeping for the same reasons.
   */
  get source(): DictionarySource {
    return this.#source;
  }

  /** Stop keeping a tier on this device, without unloading it from the page. */
  async forget(tier: ServedTier): Promise<void> {
    await this.#cache.forget(tierUrls(this.#baseUrl, tier));
  }

  /** Whether a tier is already in memory, which is what makes a switch free. */
  has(tier: ServedTier): boolean {
    return this.#loading.has(tier);
  }

  /**
   * Whether a tier is on this device already, from some earlier visit.
   *
   * The question the upgrade turns on. Somebody who has the full dictionary
   * here can be given it without being asked to agree to a download that is
   * not going to happen, and somebody who has not is not made to pay for it
   * because they once clicked a button on another day.
   */
  async isStored(tier: ServedTier): Promise<boolean> {
    return this.#cache.holds(tierUrls(this.#baseUrl, tier));
  }

  /** The dictionary for a tier, fetching it the first time. */
  load(tier: ServedTier): Promise<Dictionary> {
    const existing = this.#loading.get(tier);
    if (existing !== undefined) {
      return existing;
    }

    const loading = this.#fetch(tier);
    this.#loading.set(tier, loading);

    return loading;
  }
}
